import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, FileUp, Loader2, ScanLine } from 'lucide-react';
import Modal from '../../../components/ui/Modal';
import { ApiService } from '../../../services/api';
import type { PracticeExam } from '../../../types/practice-exam';
import DepartmentSelector from '../../../components/selectors/DepartmentSelector';

type SessionKey = 'tyt' | 'ayt';
type FileSlot = 'tytForm' | 'tytResult' | 'aytForm' | 'aytResult';

interface OpticalUploadModalProps {
    exam: PracticeExam | null;
    isOpen: boolean;
    onClose: () => void;
    onImported?: () => void;
    // Okul kartından açılınca okul seçili ve kilitli gelir (26 Eki: yükleme okulun altından)
    presetSchoolId?: string | null;
}

const SESSION_LABELS: Record<SessionKey, string> = { tyt: 'TYT', ayt: 'AYT' };
const SESSION_ORDER: SessionKey[] = ['tyt', 'ayt'];
const MATCH_LABELS: Record<string, string> = { tc: 'T.C.', phone: 'Telefon', name: 'Ad', studentNumber: 'Öğrenci no', none: 'Eşleşmedi' };
// Cevap sütununun teste nasıl bağlandığı (sunucudaki mapFieldsToSections kaynağı)
const FIELD_SOURCE_LABELS: Record<string, string> = { ad: 'alan adından', sira: 'sırayla tahmin', kayitli: 'bu form için kayıtlı', elle: 'elle seçildi' };

export const FilePicker = ({ label, accept, file, onChange, disabled }: {
    label: string;
    accept: string;
    file: File | null;
    onChange: (file: File | null) => void;
    disabled?: boolean;
}) => (
    <label className={`relative flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${file
        ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'} ${disabled ? 'pointer-events-none opacity-60' : ''}`}>
        <input
            type="file"
            accept={accept}
            disabled={disabled}
            onChange={(e) => onChange(e.target.files?.[0] || null)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
        <FileUp size={16} className="flex-shrink-0" />
        <span className="truncate">{file ? file.name : label}</span>
    </label>
);

/**
 * Optik okuyucu sonuçlarını (form tanımı .ftm/.bldm + sonuç .txt) Akademi sistemi YKS denemesine aktarır.
 * Önce önizlenir (eşleştirme, bölüm eşlemesi, uyarılar); onaylanınca yazılır.
 */
export default function OpticalUploadModal({ exam, isOpen, onClose, onImported, presetSchoolId = null }: OpticalUploadModalProps) {
    const [files, setFiles] = useState<Record<FileSlot, File | null>>({ tytForm: null, tytResult: null, aytForm: null, aytResult: null });
    const [defaultBranch, setDefaultBranch] = useState<'din' | 'felsefe'>('din');
    // Eşleştirme kapsamı: yalnız bu bölümün öğrencileri (ör. davet edilen okul); eşleşmeyen satır hesapsız (kurum dışı) kaydedilir
    const [matchDepartmentId, setMatchDepartmentId] = useState<string>('');
    // Okul etiketi: bütün satırlar bu kurum dışı okula yazılır; sonuç ve karne listelerinde "Kurum içi / okul" diye ayrılır
    const [schoolId, setSchoolId] = useState<string>('');
    const [schools, setSchools] = useState<Array<{ id: string; name: string; studentDepartmentId?: string | null }>>([]);
    useEffect(() => {
        if (!isOpen) return;
        ApiService.getExternalSchoolOptions().then((list) => setSchools(list || [])).catch(() => setSchools([]));
        if (presetSchoolId) { setSchoolId(presetSchoolId); setMatchDepartmentId(''); }
    }, [isOpen, presetSchoolId]);
    // Okulun kayıtlı öğrenci bölümü varsa eşleştirme kendiliğinden orada (sunucu da aynısını yapar); ayrıca bölüm seçilmez
    const selectedSchool = schools.find((school) => school.id === schoolId) || null;
    const schoolDepartmentId = selectedSchool?.studentDepartmentId || null;
    const [preview, setPreview] = useState<any>(null);
    const [busy, setBusy] = useState<'preview' | 'import' | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState<any>(null);
    // Öğretmenin önizlemede seçtiği sütun → test eşlemesi; değişince kaydetmeden önce yeniden önizlenmeli
    const [fieldMap, setFieldMap] = useState<Partial<Record<SessionKey, string[]>>>({});
    const [mapDirty, setMapDirty] = useState(false);
    // Yalnız öğretmenin elle değiştirdiği oturumların eşlemesi gönderilir; diğerlerini sunucu aynı kurallarla yeniden bulur
    const [editedSessions, setEditedSessions] = useState<Partial<Record<SessionKey, boolean>>>({});

    const sessions: SessionKey[] = exam?.yksMode === 'single_tyt' ? ['tyt'] : exam?.yksMode === 'single_ayt' ? ['ayt'] : ['tyt', 'ayt'];

    useEffect(() => {
        if (!isOpen) return;
        setFiles({ tytForm: null, tytResult: null, aytForm: null, aytResult: null });
        setDefaultBranch('din');
        setPreview(null);
        setError(null);
        setDone(null);
        setFieldMap({});
        setMapDirty(false);
        setEditedSessions({});
    }, [isOpen, exam?.id]);

    const setFile = (slot: FileSlot, file: File | null) => {
        setFiles((prev) => ({ ...prev, [slot]: file }));
        // Dosya değişince eski önizleme ve sütun eşlemesi geçersiz
        setPreview(null);
        setDone(null);
        const session: SessionKey = slot.startsWith('tyt') ? 'tyt' : 'ayt';
        setFieldMap((prev) => {
            const next = { ...prev };
            delete next[session];
            return next;
        });
        setEditedSessions((prev) => ({ ...prev, [session]: false }));
        setMapDirty(false);
    };

    const incompleteSession = sessions.find((session) => Boolean(files[`${session}Form`]) !== Boolean(files[`${session}Result`]));
    const hasAnySession = sessions.some((session) => files[`${session}Form`] && files[`${session}Result`]);
    const canPreview = Boolean(exam) && hasAnySession && !incompleteSession && !busy;

    const run = async (dryRun: boolean) => {
        if (!exam) return;
        setBusy(dryRun ? 'preview' : 'import');
        setError(null);
        try {
            const sentMap = Object.fromEntries(Object.entries(fieldMap).filter(([session]) => editedSessions[session as SessionKey]));
            const result = await ApiService.uploadPracticeExamOpticalResults(exam.id, files, { dryRun, defaultBranch, fieldMap: sentMap, matchDepartmentId: (!schoolDepartmentId && matchDepartmentId) || undefined, schoolId: schoolId || undefined });
            // Sunucunun uyguladığı eşleme (elle / kayıtlı / addan / sırayla) seçim kutularında gösterilir
            setFieldMap(Object.fromEntries(Object.entries(result?.sessions || {}).map(([session, info]: [string, any]) => [
                session,
                (info.fields || []).map((field: any) => field.section || ''),
            ])));
            setMapDirty(false);
            if (dryRun) setPreview(result);
            else {
                setDone(result);
                setPreview(result);
                onImported?.();
            }
        } catch (err: any) {
            setError(err?.message || 'Optik dosyalar işlenemedi');
        } finally {
            setBusy(null);
        }
    };

    const changeFieldSection = (session: SessionKey, index: number, sectionKey: string) => {
        setFieldMap((prev) => {
            const current = [...(prev[session] || [])];
            current[index] = sectionKey;
            return { ...prev, [session]: current };
        });
        setEditedSessions((prev) => ({ ...prev, [session]: true }));
        setMapDirty(true);
    };

    const matchSummary = useMemo(() => {
        if (!preview?.matching) return [];
        return Object.entries(preview.matching as Record<string, number>).filter(([, count]) => count > 0);
    }, [preview]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => !busy && onClose()}
            title={exam ? `${exam.name} - Optik Sonuç Yükle` : 'Optik Sonuç Yükle'}
            size="xl">
            <div className="space-y-4">
                <p className="text-sm text-gray-600">
                    Optik okuyucunuzun <b>form tanımı</b> (.ftm / .bldm) ve <b>sonuç dosyasını</b> (.txt) yükleyin. TYT ve AYT ayrı
                    yüklenebilir; sonradan yüklenen oturum öğrencinin mevcut sonucuyla birleşir. Öğrenciler T.C., öğrenci numarası ya da
                    adla eşleştirilir.
                </p>

                {sessions.map((session) => (
                    <div key={session} className="rounded-lg border border-gray-200 p-3">
                        <div className="mb-2 text-sm font-semibold text-gray-800">{SESSION_LABELS[session]} oturumu</div>
                        <div className="flex flex-col gap-2 sm:flex-row">
                            <FilePicker
                                label="Form tanımı (.ftm / .fmt / .bldm)"
                                accept=".ftm,.fmt,.bldm,.frm,.FTM,.FMT,.BLDM,.FRM"
                                file={files[`${session}Form`]}
                                onChange={(file) => setFile(`${session}Form`, file)}
                                disabled={Boolean(busy)}
                            />
                            <FilePicker
                                label="Sonuç dosyası (.txt)"
                                accept=".txt,.TXT,.dat,.DAT"
                                file={files[`${session}Result`]}
                                onChange={(file) => setFile(`${session}Result`, file)}
                                disabled={Boolean(busy)}
                            />
                        </div>
                    </div>
                ))}

                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                    <label className="text-sm text-gray-700">Din / felsefe bloğu boş bırakılmışsa</label>
                    <select
                        value={defaultBranch}
                        onChange={(e) => { setDefaultBranch(e.target.value as 'din' | 'felsefe'); setPreview(null); }}
                        disabled={Boolean(busy)}
                        className="rounded-md border border-gray-300 px-2 py-1.5 text-sm">
                        <option value="din">Din Kültürü say</option>
                        <option value="felsefe">Felsefe say</option>
                    </select>
                </div>

                {schoolDepartmentId ? (
                    <div className="rounded-md bg-sky-50 px-3 py-2 text-xs text-sky-800">
                        Kayıtlı öğrenciler okulun bölümünde aranır (okul kartında tanımlı); bulunamayanlar okulun kurum dışı öğrencisi olarak kaydedilir.
                    </div>
                ) : (
                <div className="flex flex-col gap-1">
                    <label className="text-sm text-gray-700">Öğrencileri yalnız şu bölümde ara (isteğe bağlı)</label>
                    <DepartmentSelector
                        value={matchDepartmentId || null}
                        onChange={(id) => { setMatchDepartmentId(id || ''); setPreview(null); }}
                        placeholder="Bütün kullanıcılar"
                        allowClear
                        disabled={Boolean(busy)}
                        className="w-full"
                    />
                    <p className="text-xs text-gray-500">
                        Başka okulun öğrencileri için o okulun bölümünü seç: kayıt olanlar T.C., telefon ya da adla kendi hesaplarına bağlanır,
                        kalanlar hesapsız (kurum dışı) kaydedilir; bizim öğrencilerimize sonuç yazılmaz.
                    </p>
                </div>
                )}

                {schools.length > 0 && (
                    <div className="flex flex-col gap-1">
                        <label className="text-sm text-gray-700">Bu sonuçlar hangi okula ait? (isteğe bağlı)</label>
                        <select
                            value={schoolId}
                            onChange={(e) => { setSchoolId(e.target.value); setPreview(null); }}
                            disabled={Boolean(busy) || Boolean(presetSchoolId)}
                            className="rounded-md border border-gray-300 px-2 py-1.5 text-sm">
                            <option value="">Kurum içi (okul yok)</option>
                            {schools.map((school) => <option key={school.id} value={school.id}>{school.name}</option>)}
                        </select>
                        <p className="text-xs text-gray-500">
                            Seçilirse bütün satırlar o okula yazılır; sonuç ve karne listelerinde okul süzgeciyle ayrılır. Daha önce yüklenmiş
                            dosyayı okul seçip yeniden yüklemek mevcut satırları yerinde etiketler.
                        </p>
                    </div>
                )}

                {incompleteSession && (
                    <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                        {SESSION_LABELS[incompleteSession]} için form tanımı ve sonuç dosyası birlikte seçilmelidir.
                    </div>
                )}
                {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

                {preview && (
                    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                        {done && (
                            <div className="flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">
                                <CheckCircle size={16} />
                                {done.imported} öğrencinin sonucu kaydedildi{done.replaced ? ` (${done.replaced} kayıt güncellendi)` : ''}. Puanlar ve sıralamalar yenilendi.
                            </div>
                        )}

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            {Object.entries(preview.sessions || {}).map(([session, info]: [string, any]) => (
                                <div key={session} className="rounded-md border border-gray-200 bg-white p-2">
                                    <div className="mb-1 text-xs font-semibold text-gray-700">
                                        {SESSION_LABELS[session as SessionKey] || session} · {info.lineCount} satır
                                    </div>
                                    <table className="w-full text-xs">
                                        <tbody>
                                            {(info.sections || []).map((section: any) => (
                                                <tr key={section.key} className="border-t border-gray-100">
                                                    <td className="py-1 pr-2 text-gray-700">{section.label}</td>
                                                    <td className="py-1 pr-2 text-right text-gray-500">{section.formQuestionCount}/{section.questionCount}</td>
                                                    <td className="py-1 text-right">
                                                        {section.warning
                                                            ? <AlertTriangle size={14} className="inline text-amber-500" />
                                                            : <CheckCircle size={14} className="inline text-emerald-500" />}
                                                        {section.withAlternative && <span className="ml-1 text-[10px] text-gray-500">din/felsefe</span>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {(info.fields || []).length > 0 && (
                                        <div className="mt-2 border-t border-gray-100 pt-2">
                                            <div className="mb-1 text-[11px] font-medium text-gray-600">Cevap sütunları</div>
                                            {(info.fields || []).map((field: any, index: number) => (
                                                <div key={index} className="flex items-center gap-2 py-0.5 text-xs">
                                                    <span className="min-w-0 flex-1 truncate text-gray-700" title={field.name}>
                                                        {field.name} <span className="text-gray-400">({field.questionCount})</span>
                                                    </span>
                                                    <select
                                                        value={fieldMap[session as SessionKey]?.[index] ?? (field.section || '')}
                                                        onChange={(e) => changeFieldSection(session as SessionKey, index, e.target.value)}
                                                        disabled={Boolean(busy) || Boolean(done)}
                                                        className="w-40 rounded border border-gray-300 px-1 py-0.5 text-xs">
                                                        <option value="">Yok say</option>
                                                        {(info.sectionOptions || []).map((option: any) => (
                                                            <option key={option.key} value={option.key}>{option.label}</option>
                                                        ))}
                                                    </select>
                                                    <span className="w-24 text-right text-[10px] text-gray-400">{FIELD_SOURCE_LABELS[field.source] || ''}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {mapDirty && (
                            <div className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800">
                                Sütun eşlemesini değiştirdiniz. Kaydetmeden önce <b>Önizle</b> ile yeniden kontrol edin; kaydettiğinizde bu form için hatırlanır.
                            </div>
                        )}

                        <div className="flex flex-wrap gap-2 text-xs">
                            <span className="rounded-full bg-white px-2.5 py-1 font-medium text-gray-700 ring-1 ring-gray-200">{preview.students?.total || 0} öğrenci</span>
                            {preview.students?.bothSessions > 0 && <span className="rounded-full bg-white px-2.5 py-1 text-gray-600 ring-1 ring-gray-200">TYT+AYT {preview.students.bothSessions}</span>}
                            {preview.students?.onlyTyt > 0 && <span className="rounded-full bg-white px-2.5 py-1 text-gray-600 ring-1 ring-gray-200">yalnız TYT {preview.students.onlyTyt}</span>}
                            {preview.students?.onlyAyt > 0 && <span className="rounded-full bg-white px-2.5 py-1 text-gray-600 ring-1 ring-gray-200">yalnız AYT {preview.students.onlyAyt}</span>}
                            {preview.students?.replacingExisting > 0 && <span className="rounded-full bg-white px-2.5 py-1 text-gray-600 ring-1 ring-gray-200">güncellenecek {preview.students.replacingExisting}</span>}
                            {preview.students?.felsefe > 0 && <span className="rounded-full bg-white px-2.5 py-1 text-gray-600 ring-1 ring-gray-200">felsefe seçen {preview.students.felsefe}</span>}
                            {preview.school && (
                                <span className="rounded-full bg-violet-50 px-2.5 py-1 text-violet-800 ring-1 ring-violet-200">okul: {preview.school.name}</span>
                            )}
                            {preview.matchScope && (
                                <span className="rounded-full bg-sky-50 px-2.5 py-1 text-sky-800 ring-1 ring-sky-200">
                                    yalnız {preview.matchScope.name} ({preview.matchScope.userCount} kişi) içinde arandı
                                </span>
                            )}
                            {matchSummary.map(([type, count]) => (
                                <span key={type} className={`rounded-full px-2.5 py-1 ring-1 ${type === 'none' ? 'bg-amber-50 text-amber-800 ring-amber-200' : 'bg-emerald-50 text-emerald-800 ring-emerald-200'}`}>
                                    {MATCH_LABELS[type] || type} {count}
                                </span>
                            ))}
                        </div>

                        {(preview.warnings?.length > 0 || preview.notes?.length > 0) && (
                            <ul className="max-h-28 list-disc space-y-0.5 overflow-y-auto rounded-md bg-amber-50 py-2 pl-6 pr-2 text-xs text-amber-800">
                                {[...(preview.warnings || []), ...(preview.notes || [])].map((warning: string, index: number) => <li key={index}>{warning}</li>)}
                            </ul>
                        )}

                        <div className="max-h-64 overflow-auto rounded-md border border-gray-200 bg-white">
                            <table className="w-full text-xs">
                                <thead className="sticky top-0 bg-gray-100 text-gray-600">
                                    <tr>
                                        <th className="px-2 py-1.5 text-left">Öğrenci</th>
                                        <th className="px-2 py-1.5 text-left">T.C.</th>
                                        <th className="px-2 py-1.5 text-left">Şube</th>
                                        <th className="px-2 py-1.5 text-left">Eşleşme</th>
                                        <th className="px-2 py-1.5 text-left">Kitapçık</th>
                                        <th className="px-2 py-1.5 text-right">Cevaplanan</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(preview.preview || []).map((row: any, index: number) => (
                                        <tr key={index} className="border-t border-gray-100">
                                            <td className="px-2 py-1 text-gray-800">
                                                {row.name}
                                                {row.matchType !== 'none' && row.opticalName && row.opticalName.toLocaleUpperCase('tr-TR') !== String(row.name).toLocaleUpperCase('tr-TR') && (
                                                    <span className="block text-[10px] text-gray-400">optikte: {row.opticalName}</span>
                                                )}
                                            </td>
                                            <td className="px-2 py-1 text-gray-500">{row.tc || '-'}</td>
                                            <td className="px-2 py-1 text-gray-500">{row.department || '-'}</td>
                                            <td className={`px-2 py-1 ${row.matchType === 'none' ? 'text-amber-700' : 'text-emerald-700'}`}>{MATCH_LABELS[row.matchType] || row.matchType}</td>
                                            <td className="px-2 py-1 text-gray-500">{SESSION_ORDER.filter((s) => row.booklets?.[s]).map((s) => `${SESSION_LABELS[s]} ${row.booklets[s]}`).join(' · ') || '-'}</td>
                                            <td className="px-2 py-1 text-right text-gray-600">{SESSION_ORDER.filter((s) => Number(row.answered?.[s]) > 0).map((s) => `${SESSION_LABELS[s]} ${row.answered[s]}`).join(' · ') || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {preview.students?.total > (preview.preview || []).length && (
                            <p className="text-xs text-gray-500">İlk {(preview.preview || []).length} öğrenci gösteriliyor.</p>
                        )}
                    </div>
                )}

                <div className="flex flex-col-reverse gap-2 border-t border-gray-200 pt-4 sm:flex-row sm:justify-end">
                    <button
                        onClick={onClose}
                        disabled={Boolean(busy)}
                        className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50">
                        {done ? 'Kapat' : 'İptal'}
                    </button>
                    <button
                        onClick={() => run(true)}
                        disabled={!canPreview}
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-blue-600 bg-white px-4 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50">
                        {busy === 'preview' ? <Loader2 size={16} className="animate-spin" /> : <ScanLine size={16} />}
                        Önizle
                    </button>
                    <button
                        onClick={() => run(false)}
                        disabled={!preview || Boolean(done) || !canPreview || mapDirty}
                        className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
                        {busy === 'import' ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                        Sonuçları Kaydet
                    </button>
                </div>
            </div>
        </Modal>
    );
}
